import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
  useMemo,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import type {
  InstitutionSelectorProps,
  InstitutionOption,
  InstitutionGroup,
} from './types'
import styles from './InstitutionSelector.module.css'

/** Opção achatada com groupLabel para renderizar em grupos */
interface FlatOption extends InstitutionOption {
  groupLabel: string
}

function flattenGroups(groups: InstitutionGroup[]): FlatOption[] {
  return groups.flatMap((g) =>
    g.options.map((opt) => ({ ...opt, groupLabel: g.label }))
  )
}

function filterOptions(
  flat: FlatOption[],
  search: string
): { groupLabel: string; options: InstitutionOption[] }[] {
  const q = search.trim().toLowerCase()
  if (!q) {
    return flat.reduce<{ groupLabel: string; options: InstitutionOption[] }[]>(
      (acc, opt) => {
        const last = acc[acc.length - 1]
        if (last?.groupLabel === opt.groupLabel) {
          last.options.push(opt)
        } else {
          acc.push({ groupLabel: opt.groupLabel, options: [opt] })
        }
        return acc
      },
      []
    )
  }
  const filtered = flat.filter(
    (opt) =>
      opt.label.toLowerCase().includes(q) ||
      (opt.subtitle?.toLowerCase().includes(q) ?? false)
  )
  return filtered.reduce<
    { groupLabel: string; options: InstitutionOption[] }[]
  >((acc, opt) => {
    const last = acc[acc.length - 1]
    if (last?.groupLabel === opt.groupLabel) {
      last.options.push(opt)
    } else {
      acc.push({ groupLabel: opt.groupLabel, options: [opt] })
    }
    return acc
  }, [])
}

export function InstitutionSelector({
  value,
  onChange,
  groups,
  placeholder = 'Selecione uma instituição',
  disabled = false,
  className,
  id,
}: InstitutionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const flatOptions = useMemo(() => flattenGroups(groups), [groups])
  const selectedOption = useMemo(
    () => flatOptions.find((opt) => opt.value === value),
    [flatOptions, value]
  )
  const filteredGroups = useMemo(
    () => filterOptions(flatOptions, search),
    [flatOptions, search]
  )
  const allFilteredOptions = useMemo(
    () => filteredGroups.flatMap((g) => g.options),
    [filteredGroups]
  )

  const generatedId = useId()
  const selectId = id ?? generatedId
  const listId = `${selectId}-list`
  const [listPosition, setListPosition] = useState({ top: 0, left: 0, width: 0 })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        listRef.current &&
        !listRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearch('')
        setHighlightedKey(null)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setListPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 320),
      })
    }
  }, [isOpen])

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev)
      if (!isOpen) {
        setSearch('')
        setHighlightedKey(value ?? null)
      }
    }
  }, [disabled, isOpen, value])

  const handleSelect = useCallback(
    (optionValue: string) => {
      if (!disabled) {
        onChange?.(optionValue)
        setIsOpen(false)
        setSearch('')
        setHighlightedKey(null)
        triggerRef.current?.focus()
      }
    },
    [disabled, onChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
        setSearch('')
        setHighlightedKey(null)
        triggerRef.current?.focus()
        return
      }
      if (e.key === 'Tab' && isOpen) {
        setIsOpen(false)
        setSearch('')
        setHighlightedKey(null)
        return
      }
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleToggle()
        }
        return
      }
      if (e.key === 'Enter' && highlightedKey) {
        e.preventDefault()
        handleSelect(highlightedKey)
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const idx = allFilteredOptions.findIndex((o) => o.value === highlightedKey)
        let nextIdx: number
        if (e.key === 'ArrowDown') {
          nextIdx = idx < allFilteredOptions.length - 1 ? idx + 1 : 0
        } else {
          nextIdx = idx > 0 ? idx - 1 : allFilteredOptions.length - 1
        }
        setHighlightedKey(allFilteredOptions[nextIdx]?.value ?? null)
      }
    },
    [
      disabled,
      isOpen,
      highlightedKey,
      allFilteredOptions,
      handleToggle,
      handleSelect,
    ]
  )

  const dropdownContent = isOpen ? (
    <div
      ref={listRef}
      id={listId}
      role="listbox"
      aria-label="Instituições"
      className={styles.dropdown}
      style={{
        top: `${listPosition.top}px`,
        left: `${listPosition.left}px`,
        width: `${listPosition.width}px`,
      }}
    >
      <div className={styles.searchWrap}>
        <Icon icon="mdi:magnify" className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Buscar instituição..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSearch('')
              triggerRef.current?.focus()
            }
          }}
          autoFocus
          aria-label="Buscar instituição"
        />
      </div>
      <div className={styles.groups}>
        {filteredGroups.map((group) => (
          <div key={group.groupLabel} className={styles.group}>
            <div className={styles.groupLabel}>{group.groupLabel}</div>
            {group.options.map((option) => {
              const isSelected = option.value === value
              const isHighlighted = option.value === highlightedKey
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={[
                    styles.option,
                    isSelected && styles.optionSelected,
                    isHighlighted && styles.optionHighlighted,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedKey(option.value)}
                >
                  <span className={styles.optionIconWrap}>
                    {option.icon ? (
                      <Icon icon={option.icon} className={styles.optionIcon} />
                    ) : (
                      <Icon
                        icon="mdi:office-building"
                        className={styles.optionIcon}
                      />
                    )}
                  </span>
                  <span className={styles.optionText}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.subtitle && (
                      <span className={styles.optionSubtitle}>
                        {option.subtitle}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <Icon icon="mdi:check" className={styles.optionCheck} />
                  )}
                </button>
              )
            })}
          </div>
        ))}
        {filteredGroups.length === 0 && (
          <div className={styles.empty}>Nenhuma instituição encontrada</div>
        )}
      </div>
    </div>
  ) : null

  return (
    <div
      ref={containerRef}
      className={[styles.container, className].filter(Boolean).join(' ')}
    >
      <button
        ref={triggerRef}
        id={selectId}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-disabled={disabled}
        aria-label={`Instituição selecionada: ${selectedOption?.label ?? placeholder}`}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={[styles.trigger, isOpen && styles.triggerOpen]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.triggerIconWrap}>
          {selectedOption?.icon ? (
            <Icon
              icon={selectedOption.icon}
              className={styles.triggerIcon}
            />
          ) : (
            <Icon
              icon="mdi:office-building"
              className={styles.triggerIcon}
            />
          )}
        </span>
        <span className={styles.triggerText}>
          <span className={styles.triggerLabel}>Instituição selecionada</span>
          <span className={styles.triggerValue}>
            {selectedOption?.label ?? placeholder}
          </span>
        </span>
        <Icon
          icon="mdi:chevron-down"
          className={[styles.chevron, isOpen && styles.chevronOpen]
            .filter(Boolean)
            .join(' ')}
        />
      </button>
      {typeof document !== 'undefined' &&
        createPortal(dropdownContent, document.body)}
    </div>
  )
}
